import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import ProductCard from '../shop/ProductCard';

const ProductCarouselRow = ({
  title,
  subtitle,
  tag,
  icon: Icon,
  badgeBg = 'bg-emerald-100 text-emerald-900',
  iconColor = 'text-emerald-700',
  products = [],
  onViewAll,
  autoScrollInterval = 2000,
  categoryId = 'all'
}) => {
  const { lang, isRtl } = useLanguage();
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // If no products in this section, don't render an empty section
  if (!products || products.length === 0) {
    return null;
  }

  // Check scroll position to update arrow states
  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll <= 5) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    } else {
      setCanScrollLeft(true);
      setCanScrollRight(true);
    }
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        el.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [updateScrollButtons, products]);

  // Scroll forward (in reading direction: in RTL moves Left, in LTR moves Right)
  const scrollNext = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const cardWidth = 300; // approximate card width + gap
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) return;

    if (isRtl) {
      // In RTL, moving forward means scrolling towards the left
      const isAtEnd = Math.abs(scrollLeft) >= maxScroll - 20;
      if (isAtEnd) {
        // Loop back to start
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: -cardWidth, behavior: 'smooth' });
      }
    } else {
      // In LTR, moving forward means scrolling towards the right
      const isAtEnd = scrollLeft >= maxScroll - 20;
      if (isAtEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    }
  }, [isRtl]);

  // Scroll backward
  const scrollPrev = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const cardWidth = 300;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) return;

    if (isRtl) {
      const isAtStart = Math.abs(scrollLeft) <= 20;
      if (isAtStart) {
        // Loop to end
        el.scrollTo({ left: -maxScroll, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    } else {
      const isAtStart = scrollLeft <= 20;
      if (isAtStart) {
        el.scrollTo({ left: maxScroll, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: -cardWidth, behavior: 'smooth' });
      }
    }
  }, [isRtl]);

  // Auto-scroll effect: every 2 seconds
  useEffect(() => {
    if (isHovered || products.length <= 1) return;

    const interval = setInterval(() => {
      scrollNext();
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [isHovered, scrollNext, autoScrollInterval, products.length]);

  return (
    <div
      className="relative group/carousel py-8 border-b border-gray-100 last:border-b-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => {
        // Resume after 3 seconds of touch end
        setTimeout(() => setIsHovered(false), 3000);
      }}
    >
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          {tag && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-title mb-2 ${badgeBg}`}>
              {Icon && <Icon className={`w-3.5 h-3.5 ${iconColor}`} />}
              <span>{tag}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-pansar-950 font-title">
              {title}
            </h3>
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-pansar-50 text-pansar-800 border border-pansar-100 font-sans">
              {products.length} {lang === 'ur' ? 'ادویات' : 'items'}
            </span>
          </div>
          {subtitle && (
            <p className="text-xs sm:text-sm text-gray-500 font-urdu mt-1 max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>

        {/* Action Controls: Navigation Arrows + View All button */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* Left Arrow Button */}
          <button
            type="button"
            onClick={isRtl ? scrollNext : scrollPrev}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white hover:bg-pansar-50 text-pansar-900 border border-pansar-200 shadow-xs flex items-center justify-center transition active:scale-95 cursor-pointer hover:border-pansar-400 hover:shadow-md"
            title={lang === 'ur' ? 'بائیں طرف سکرول کریں' : 'Scroll Left'}
            aria-label="Scroll Left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Right Arrow Button */}
          <button
            type="button"
            onClick={isRtl ? scrollPrev : scrollNext}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white hover:bg-pansar-50 text-pansar-900 border border-pansar-200 shadow-xs flex items-center justify-center transition active:scale-95 cursor-pointer hover:border-pansar-400 hover:shadow-md"
            title={lang === 'ur' ? 'دائیں طرف سکرول کریں' : 'Scroll Right'}
            aria-label="Scroll Right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* View All Button */}
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="px-4 py-2 sm:py-2.5 rounded-2xl bg-pansar-50 hover:bg-pansar-100 text-pansar-900 font-bold text-xs flex items-center gap-1.5 font-urdu transition border border-pansar-200 cursor-pointer ml-1"
            >
              <span>{lang === 'ur' ? 'سب دیکھیں' : 'View All'}</span>
              {isRtl ? <ArrowLeft className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Horizontal Carousel Track Container */}
      <div className="relative">
        
        {/* Floating Side Left Arrow (for quick 1-click on larger screens) */}
        <button
          type="button"
          onClick={isRtl ? scrollNext : scrollPrev}
          className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/95 backdrop-blur-xs text-pansar-900 border border-gray-200 shadow-xl items-center justify-center transition opacity-0 group-hover/carousel:opacity-100 hover:bg-pansar-800 hover:text-white hover:scale-110 active:scale-95 cursor-pointer"
          aria-label="Scroll Left"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Floating Side Right Arrow (for quick 1-click on larger screens) */}
        <button
          type="button"
          onClick={isRtl ? scrollPrev : scrollNext}
          className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/95 backdrop-blur-xs text-pansar-900 border border-gray-200 shadow-xl items-center justify-center transition opacity-0 group-hover/carousel:opacity-100 hover:bg-pansar-800 hover:text-white hover:scale-110 active:scale-95 cursor-pointer"
          aria-label="Scroll Right"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Scrollable Track */}
        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-5 overflow-x-auto scroll-smooth pb-4 pt-1 px-1 scrollbar-none snap-x snap-mandatory"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="w-[260px] sm:w-[280px] lg:w-[295px] shrink-0 snap-start"
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCarouselRow;
